import React, { useState } from 'react';
import { Transition, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { Check, Copy } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';

interface PasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  password?: string;
  title: string;
}

/**
 * Shows freshly-generated credentials (email + password) after inviting a member
 * or resetting a password, with a one-click copy button for the password.
 */
export const PasswordModal: React.FC<PasswordModalProps> = ({ isOpen, onClose, email, password, title }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (password) {
      navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                data-test-id="member-password-modal"
                className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
              >
                <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    {t('settingsMember.passwordModal.description')}
                  </p>
                  <div className="bg-gray-50 p-4 rounded-md space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">{t('settingsMember.fields.email')}</label>
                      <div className="mt-1 text-sm font-mono text-gray-900">{email}</div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">{t('settingsMember.fields.password')}</label>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-sm font-mono text-gray-900 font-bold tracking-wide">{password}</span>
                        <button
                          data-test-id="member-password-copy-button"
                          onClick={handleCopy}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title={t('settingsMember.actions.copyPassword')}
                        >
                          {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    data-test-id="member-password-done-button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-primary-100 px-4 py-2 text-sm font-medium text-primary-900 hover:bg-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 w-full"
                    onClick={onClose}
                  >
                    {t('settingsMember.actions.done')}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
